// CTA targets for the credential-sharing (Apple Developer account) nag flow.
// No URLs are hardcoded in the guard/modal/hook — they all read from here so
// OPE-817 / OPE-908 can swap targets without touching component code.

// The Apple self-serve destination. Membership renewal AND license-agreement
// acceptance both happen in App Store Connect, so a single constant covers the
// self-serve path.
export const APP_STORE_CONNECT_URL = 'https://appstoreconnect.apple.com/'

// Where a merchant goes to sign the LPA / authorize Thanx to act on their
// behalf. Falls back to a support-contact action when no signing URL is
// configured yet (mechanism still undecided — see OPE-817).
export const LPA_SUPPORT_EMAIL = 'success@thanx.com'

export type LpaTarget = { kind: 'url'; url: string } | { kind: 'contact' }

/**
 * Resolves the "let Thanx handle it" (grant-authority) CTA target.
 * - REACT_APP_LPA_AGREEMENT_URL set -> `url` mode (link out to the signing flow).
 * - otherwise -> `contact` mode (mailto the support address).
 */
export function getLpaTarget(): LpaTarget {
  const url = process.env.REACT_APP_LPA_AGREEMENT_URL
  return url ? { kind: 'url', url } : { kind: 'contact' }
}

/**
 * Builds the support mailto for the `contact`-mode grant CTA. Attributed with
 * the merchant's own brand NAME so success@ gets an identifiable request
 * instead of a blank compose window — but never the internal merchant id,
 * which must not leak into a merchant-facing surface. Ops identifies the
 * account from the sender's address; the name is only a friendly subject hint.
 */
export function buildLpaSupportMailto(
  merchant?: { name?: string } | null
): string {
  const name = merchant?.name?.trim()
  const subject = name
    ? `Apple Developer account — let Thanx handle it — ${name}`
    : 'Apple Developer account — let Thanx handle it'
  const opener = name ? `${name} would` : 'We would'
  const body =
    `Hi Thanx team,\n\n${opener} like Thanx to handle the Apple ` +
    `Developer account items (review & sign the credential-sharing ` +
    `authorization).\n\nThanks!`
  return (
    `mailto:${LPA_SUPPORT_EMAIL}` +
    `?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  )
}
